/**
 * Jubly Calendar Event Modal — Google Calendar style
 * Clean event creation/editing dialog
 */

import { useState, useEffect } from 'react';
import {
  X,
  Clock,
  MapPin,
  AlignLeft,
  Users,
  Repeat,
  Trash2,
  Plus,
  Check,
  XCircle,
  HelpCircle,
  ChevronDown
} from 'lucide-react';

const RECURRENCE_OPTIONS = [
  { value: null, label: 'Does not repeat' },
  { value: 'FREQ=DAILY', label: 'Daily' },
  { value: 'FREQ=WEEKLY', label: 'Weekly' },
  { value: 'FREQ=WEEKLY;BYDAY=MO,WE,FR', label: 'Mon, Wed, Fri' },
  { value: 'FREQ=WEEKLY;BYDAY=MO,TU,WE,TH,FR', label: 'Every weekday' },
  { value: 'FREQ=MONTHLY', label: 'Monthly' },
  { value: 'FREQ=YEARLY', label: 'Yearly' }
];

export default function EventModal({
  isOpen,
  onClose,
  onSave,
  onDelete,
  event = null,
  initialDate = null,
  initialEndDate = null,
  calendars = []
}) {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    location: '',
    calendarId: '',
    startDate: '',
    startTime: '',
    endDate: '',
    endTime: '',
    isAllDay: false,
    recurrenceRule: null,
    attendees: [],
    projectId: null
  });
  const [workProjects, setWorkProjects] = useState([]);

  const [newAttendee, setNewAttendee] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showMore, setShowMore] = useState(false);
  const [contactSuggestions, setContactSuggestions] = useState([]);
  const [focusedSuggestion, setFocusedSuggestion] = useState(-1);
  const suggestTimer = { current: null };

  useEffect(() => {
    if (isOpen) {
      // Fetch work projects for the project picker
      fetch('/api/work/projects', { credentials: 'include' })
        .then(r => r.ok ? r.json() : { projects: [] })
        .then(d => setWorkProjects(d.projects || []))
        .catch(() => {});

      if (event) {
        const start = new Date(event.start_time);
        const end = new Date(event.end_time);
        setFormData({
          title: event.title || '',
          description: event.description || '',
          location: event.location || '',
          calendarId: event.calendar_id || calendars[0]?.id || '',
          startDate: start.toISOString().split('T')[0],
          startTime: start.toTimeString().slice(0, 5),
          endDate: end.toISOString().split('T')[0],
          endTime: end.toTimeString().slice(0, 5),
          isAllDay: event.is_all_day || false,
          recurrenceRule: event.recurrence_rule || null,
          attendees: event.attendees || [],
          projectId: event.project_id || null
        });
        setShowMore(!!(event.location || event.description));
      } else if (initialDate) {
        const start = new Date(initialDate);
        const end = initialEndDate ? new Date(initialEndDate) : new Date(start);
        if (!initialEndDate) end.setHours(end.getHours() + 1);
        setFormData({
          title: '',
          description: '',
          location: '',
          calendarId: calendars.find(c => c.is_default)?.id || calendars[0]?.id || '',
          startDate: start.toISOString().split('T')[0],
          startTime: start.toTimeString().slice(0, 5),
          endDate: end.toISOString().split('T')[0],
          endTime: end.toTimeString().slice(0, 5),
          isAllDay: false,
          recurrenceRule: null,
          attendees: [],
          projectId: null
        });
        setShowMore(false);
      }
    }
  }, [isOpen, event, initialDate, initialEndDate, calendars]);

  if (!isOpen) return null;

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setIsSubmitting(true);
    try {
      const startTime = new Date(`${formData.startDate}T${formData.isAllDay ? '00:00' : formData.startTime}`);
      const endTime = new Date(`${formData.endDate}T${formData.isAllDay ? '23:59' : formData.endTime}`);
      await onSave({
        calendarId: formData.calendarId,
        title: formData.title,
        description: formData.description,
        location: formData.location,
        startTime: startTime.toISOString(),
        endTime: endTime.toISOString(),
        isAllDay: formData.isAllDay,
        recurrenceRule: formData.recurrenceRule,
        attendees: formData.attendees.map(a => ({ email: a.email, name: a.name, role: a.role || 'attendee' })),
        projectId: formData.projectId || undefined
      });
      onClose();
    } catch (err) {
      console.error('Failed to save event:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleAddAttendee = (emailOverride) => {
    const raw = (emailOverride || newAttendee).trim();
    if (!raw) return;
    let email = raw;
    if (!email.includes('@')) email = `${email}@jubly.me`;
    if (!formData.attendees.find(a => a.email === email)) {
      setFormData(prev => ({
        ...prev,
        attendees: [...prev.attendees, { email, name: email.split('@')[0], role: 'attendee', status: 'pending' }]
      }));
    }
    setNewAttendee('');
    setContactSuggestions([]);
    setFocusedSuggestion(-1);
  };

  const fetchContactSuggestions = (q) => {
    clearTimeout(suggestTimer.current);
    if (q.length < 2) { setContactSuggestions([]); return; }
    suggestTimer.current = setTimeout(async () => {
      try {
        const current = formData.attendees.map(a => a.email).join(',');
        const res = await fetch(`/api/contacts/suggest?q=${encodeURIComponent(q)}&current=${encodeURIComponent(current)}`, { credentials: 'include' });
        if (!res.ok) throw new Error();
        const data = await res.json();
        const contacts = (data.contacts || []).filter(c => !formData.attendees.find(a => a.email === (c.email || c.contact_email)));
        setContactSuggestions(contacts);
        setFocusedSuggestion(-1);
      } catch { setContactSuggestions([]); }
    }, 200);
  };

  const selectedCal = calendars.find(c => c.id === formData.calendarId);

  const formatDateLabel = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr + 'T00:00:00');
    return d.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' });
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-start justify-center z-50 pt-[10vh]">
      <div className="bg-white rounded-lg shadow-2xl w-full max-w-[448px] flex flex-col overflow-hidden">
        {/* Header — colored bar */}
        <div
          className="h-2 rounded-t-lg"
          style={{ backgroundColor: selectedCal?.color || '#3B82F6' }}
        />

        {/* Close button row */}
        <div className="flex justify-end px-3 pt-2">
          {event && (
            <button
              onClick={() => onDelete?.(event.id)}
              className="p-2 text-gray-500 hover:bg-gray-100 rounded-full transition-colors"
            >
              <Trash2 className="w-5 h-5" />
            </button>
          )}
          <button
            onClick={onClose}
            className="p-2 text-gray-500 hover:bg-gray-100 rounded-full transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="px-6 pb-4 space-y-4">
          {/* Title — large input like Google Calendar */}
          <input
            type="text"
            placeholder="Add title"
            value={formData.title}
            onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
            className="w-full text-[22px] font-normal text-gray-800 placeholder-gray-400 border-b-2 border-gray-200 focus:border-blue-600 pb-2 outline-none transition-colors"
            required
            autoFocus
          />

          {/* Date & Time */}
          <div className="flex items-center gap-3">
            <Clock className="w-5 h-5 text-gray-400 flex-shrink-0" />
            <div className="flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                <button
                  type="button"
                  className="text-sm text-blue-600 hover:bg-blue-50 px-2 py-1 rounded transition-colors"
                  onClick={() => {/* Could open date picker */}}
                >
                  {formatDateLabel(formData.startDate)}
                </button>
                {!formData.isAllDay && (
                  <>
                    <input
                      type="time"
                      value={formData.startTime}
                      onChange={(e) => setFormData(prev => ({ ...prev, startTime: e.target.value }))}
                      className="text-sm px-2 py-1 border border-gray-200 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500 outline-none"
                    />
                    <span className="text-gray-400">–</span>
                    <input
                      type="time"
                      value={formData.endTime}
                      onChange={(e) => setFormData(prev => ({ ...prev, endTime: e.target.value }))}
                      className="text-sm px-2 py-1 border border-gray-200 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500 outline-none"
                    />
                  </>
                )}
              </div>
              {/* Hidden date inputs for actual values */}
              <input type="date" value={formData.startDate} onChange={(e) => setFormData(prev => ({ ...prev, startDate: e.target.value, endDate: e.target.value }))} className="sr-only" />
              <input type="date" value={formData.endDate} onChange={(e) => setFormData(prev => ({ ...prev, endDate: e.target.value }))} className="sr-only" />

              <div className="flex items-center gap-4 mt-2">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.isAllDay}
                    onChange={(e) => setFormData(prev => ({ ...prev, isAllDay: e.target.checked }))}
                    className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                  />
                  <span className="text-sm text-gray-600">All day</span>
                </label>

                <select
                  value={formData.recurrenceRule || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, recurrenceRule: e.target.value || null }))}
                  className="text-sm text-gray-600 bg-transparent border-none focus:ring-0 cursor-pointer py-0"
                >
                  {RECURRENCE_OPTIONS.map(opt => (
                    <option key={opt.value || 'none'} value={opt.value || ''}>{opt.label}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* Attendees */}
          <div className="flex items-start gap-3">
            <Users className="w-5 h-5 text-gray-400 mt-2 flex-shrink-0" />
            <div className="flex-1">
              <div className="relative">
                <input
                  type="text"
                  placeholder="Add guests"
                  value={newAttendee}
                  onChange={(e) => { setNewAttendee(e.target.value); fetchContactSuggestions(e.target.value); }}
                  onKeyDown={(e) => {
                    if (contactSuggestions.length > 0) {
                      if (e.key === 'ArrowDown') { e.preventDefault(); setFocusedSuggestion(i => Math.min(i + 1, contactSuggestions.length - 1)); return; }
                      if (e.key === 'ArrowUp') { e.preventDefault(); setFocusedSuggestion(i => Math.max(i - 1, 0)); return; }
                      if (e.key === 'Enter' && focusedSuggestion >= 0) { e.preventDefault(); handleAddAttendee(contactSuggestions[focusedSuggestion].email || contactSuggestions[focusedSuggestion].contact_email); return; }
                      if (e.key === 'Escape') { setContactSuggestions([]); return; }
                    }
                    if (e.key === 'Enter') { e.preventDefault(); handleAddAttendee(); }
                  }}
                  onBlur={() => setTimeout(() => setContactSuggestions([]), 200)}
                  className="w-full text-sm px-2 py-1.5 border border-gray-200 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500 outline-none"
                />
                {contactSuggestions.length > 0 && (
                  <div className="absolute left-0 top-full z-50 mt-1 w-full max-h-48 overflow-y-auto rounded-lg border border-gray-200 bg-white shadow-lg">
                    {contactSuggestions.map((c, i) => {
                      const email = c.email || c.contact_email;
                      const name = c.name || c.contact_name;
                      return (
                        <button key={email} type="button"
                          onMouseDown={(e) => { e.preventDefault(); handleAddAttendee(email); }}
                          className={`flex w-full items-center gap-2 px-3 py-2 text-left text-sm transition-colors ${i === focusedSuggestion ? 'bg-blue-50 text-blue-700' : 'text-gray-700 hover:bg-gray-50'}`}>
                          <div className="w-6 h-6 rounded-full bg-gray-100 flex items-center justify-center text-xs font-bold text-gray-500 flex-shrink-0">
                            {(name || email || '?').charAt(0).toUpperCase()}
                          </div>
                          <div className="min-w-0 flex-1">
                            {name && <p className="truncate text-sm font-medium">{name}</p>}
                            <p className="truncate text-xs text-gray-500">{email}</p>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
              {formData.attendees.length > 0 && (
                <div className="mt-2 space-y-1">
                  {formData.attendees.map(a => (
                    <div key={a.email} className="flex items-center justify-between py-1.5">
                      <div className="flex items-center gap-2 min-w-0">
                        <div className="w-7 h-7 rounded-full bg-blue-100 flex items-center justify-center text-xs text-blue-700 font-semibold flex-shrink-0">
                          {(a.name || a.email).charAt(0).toUpperCase()}
                        </div>
                        <div className="min-w-0">
                          <div className="text-sm text-gray-800 font-medium truncate">{a.name}</div>
                          <div className="text-xs text-gray-400 truncate">{a.email}</div>
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => setFormData(prev => ({ ...prev, attendees: prev.attendees.filter(x => x.email !== a.email) }))}
                        className="text-gray-300 hover:text-gray-500 flex-shrink-0 ml-2"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                  {/* Meeting auto-created indicator + optional project */}
                  <div className="mt-2 py-1.5 px-2 bg-blue-50 rounded-lg space-y-2">
                    <div className="flex items-center gap-2">
                      <svg className="w-4 h-4 text-blue-500 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                      </svg>
                      <span className="text-xs text-blue-700 font-medium">Video meeting will be added automatically</span>
                    </div>
                    {workProjects.length > 0 && (
                      <div className="flex items-center gap-2">
                        <svg className="w-4 h-4 text-blue-400 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7a2 2 0 012-2h4l2 2h8a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2V7z" />
                        </svg>
                        <select
                          value={formData.projectId || ''}
                          onChange={(e) => setFormData(prev => ({ ...prev, projectId: e.target.value || null }))}
                          className="text-xs text-blue-700 bg-transparent border-none focus:ring-0 cursor-pointer py-0 flex-1"
                        >
                          <option value="">No project</option>
                          {workProjects.map(p => (
                            <option key={p.id} value={p.id}>{p.name}</option>
                          ))}
                        </select>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Location & Description (expandable) */}
          {!showMore ? (
            <button
              type="button"
              onClick={() => setShowMore(true)}
              className="flex items-center gap-2 text-sm text-gray-500 hover:text-gray-700 transition-colors"
            >
              <Plus className="w-4 h-4" />
              Add location, description
            </button>
          ) : (
            <>
              <div className="flex items-center gap-3">
                <MapPin className="w-5 h-5 text-gray-400 flex-shrink-0" />
                <input
                  type="text"
                  placeholder="Add location"
                  value={formData.location}
                  onChange={(e) => setFormData(prev => ({ ...prev, location: e.target.value }))}
                  className="flex-1 text-sm px-2 py-1.5 border border-gray-200 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500 outline-none"
                />
              </div>
              <div className="flex items-start gap-3">
                <AlignLeft className="w-5 h-5 text-gray-400 mt-2 flex-shrink-0" />
                <textarea
                  placeholder="Add description"
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  rows={2}
                  className="flex-1 text-sm px-2 py-1.5 border border-gray-200 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500 outline-none resize-none"
                />
              </div>
            </>
          )}

          {/* Calendar selector */}
          <div className="flex items-center gap-3">
            <div
              className="w-3.5 h-3.5 rounded-sm flex-shrink-0"
              style={{ backgroundColor: selectedCal?.color || '#3B82F6' }}
            />
            <select
              value={formData.calendarId}
              onChange={(e) => setFormData(prev => ({ ...prev, calendarId: e.target.value }))}
              className="text-sm text-gray-700 bg-transparent border-none focus:ring-0 cursor-pointer py-0"
            >
              {calendars.map(cal => (
                <option key={cal.id} value={cal.id}>{cal.name}</option>
              ))}
            </select>
          </div>
        </form>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 px-6 py-3 border-t border-gray-100">
          <button
            type="button"
            onClick={handleSubmit}
            disabled={isSubmitting || !formData.title}
            className="px-6 py-2 bg-blue-600 text-white rounded-full text-sm font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {isSubmitting ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}
